<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import Analytics from '@/views/linkarme/Admin/Analytics.vue';
import { useLoading } from '@/composables/useLoading';
import SimpleOverviewSection from '@/components/overview/SimpleOverviewSection.vue';
import RecommendedSitesSection from '@/components/dashboard/RecommendedSitesSection.vue';

const props = defineProps({
    data: Array,
    list: Array,
    news: Array,
    newsCount: Number,
    totalSitesCount: Number,
    recommended: Array,
    topSellers: Array,
    coins: Object,
    orders: Number,
    ordersPercent: Number,
    ordersPercentIncrease: Boolean,
    sitesAvailablePercent: Number,
    sitesAvailablePercentIncrease: Boolean,
    sitesPercent: Number,
    sitesPercentIncrease: Boolean,
    usedCount: Number,
    unusedCount: Number,
    recommendedCount: Number,
    notifications: Array,
    favorites: Array,
    interests: Array,
    projects: Array,
    sitesByCountry: Array,
    sitesByCountrySvg: Array,
    overviewBanner: Object,
    overviewNews: Array,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();

// Dashboard metrics data baseado no Client mas adaptado para Admin
const totalValueData = ref([
    {
        title: 'Total Orders',
        value: props.orders || 0,
        comparedToLastYear: 8.53,
        percent: props.ordersPercent,
        increase: props.ordersPercentIncrease,
        icon: 'pi pi-shopping-cart',
        color: 'primary-active',
    },
    {
        title: 'Sites Used',
        value: props.usedCount || 0,
        comparedToLastYear: 5.23,
        percent: props.sitesPercent,
        increase: props.sitesPercentIncrease,
        icon: 'pi pi-check-circle',
        color: 'emerald-600',
    },
    {
        title: 'Available Sites',
        value: props.unusedCount || 0,
        comparedToLastYear: 15.42,
        percent: props.sitesAvailablePercent,
        increase: props.sitesAvailablePercentIncrease,
        icon: 'pi pi-globe',
        color: 'orange-600',
    },
    {
        title: 'Recommended Sites',
        value: props.recommendedCount || 0,
        comparedToLastYear: 10.5,
        percent: 15,
        increase: true,
        icon: 'pi pi-star-fill',
        color: 'yellow-600',
    }
]);

// Adaptar dados do DashboardNew.vue antigo para PrimeVue
const adminStats = computed(() => {
    if (!props.data) return [];
    return props.data.map(item => ({
        label: item.label,
        value: item.value,
        link: item.link
    }));
});

const adminTables = computed(() => {
    if (!props.list) return [];
    return props.list.flat().map(table => ({
        title: table.label,
        columns: table.columns,
        data: table.value,
        attributes: table.attributes,
        link: table.link
    }));
});

// Recent activity data
const recentActivity = computed(() => [
    ...props.news.slice(0, 3).map(site => ({
        id: site.id,
        title: site.real_url || site.url,
        description: $t('New site available'),
        time: new Date().toISOString(),
        icon: 'pi pi-plus-circle',
        iconClass: 'text-green-600',
        type: 'new-site'
    }))
].slice(0, 6));

const quickStats = computed(() => [
    {
        label: $t('New Sites'),
        value: props.newsCount ?? 0,
        icon: 'pi pi-sparkles',
        color: 'text-blue-500',
        bgColor: 'bg-blue-50'
    },
    {
        label: $t('Recommended'),
        // value: props.recommended?.length || 0,
        value: props.recommendedCount || 0,
        icon: 'pi pi-star',
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-50'
    },
    {
        label: $t('Total Sites'),
        value: props.totalSitesCount ?? 0,
        icon: 'pi pi-globe',
        color: 'text-green-500',
        bgColor: 'bg-green-50'
    }
]);

const refreshing = ref(false);

const refreshData = () => {
    refreshing.value = true;
    router.post(route('dashboard.refresh-cache'), {}, {
        preserveScroll: true,
        onFinish: () => {
            refreshing.value = false;
        },
    });
};

defineOptions({
    layout: AdminMainLayout,
});
</script>

<template>
    <Head :title="'Admin - ' + $t('Dashboard')" />

    <!-- Overview Section with Hero Banner and News -->
    <SimpleOverviewSection :banner="overviewBanner" :news="overviewNews" />

    <div class="grid grid-cols-12 gap-6 px-6 py-3">
        <!-- Header with refresh button -->
        <div class="col-span-12 flex items-center justify-end">
            <Button
                :label="$t('Refresh data')"
                icon="pi pi-refresh"
                severity="secondary"
                outlined
                size="small"
                :loading="refreshing"
                @click="refreshData"
            />
        </div>

        <!-- Admin Stats Cards (from old DashboardNew) -->
<!--        <div v-if="adminStats.length > 0" class="col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">-->
<!--            <Link-->
<!--                v-for="(stat, index) in adminStats"-->
<!--                :key="index"-->
<!--                :href="stat.link"-->
<!--                @click="() => showLoading($t('Loading...'))"-->
<!--                class="bg-white rounded-xl p-6 shadow-sm border border-surface-200 hover:shadow-md transition-shadow block"-->
<!--            >-->
<!--                <div class="flex items-center justify-between">-->
<!--                    <div>-->
<!--                        <p class="text-2xl font-bold text-surface-900">{{ stat.value }}</p>-->
<!--                        <p class="text-sm text-surface-600 font-medium mt-1">{{ stat.label }}</p>-->
<!--                    </div>-->
<!--                    <i class="pi pi-arrow-right text-primary text-lg"></i>-->
<!--                </div>-->
<!--            </Link>-->
<!--        </div>-->

        <!-- Key Metrics Cards -->
        <div class="col-span-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <div
                v-for="metric in totalValueData"
                :key="metric.title"
                class="bg-white rounded-xl p-6 shadow-sm border border-surface-200 hover:shadow-md transition-shadow"
            >
                <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center space-x-3">
                        <div :class="[
                            'w-12 h-12 rounded-lg flex items-center justify-center',
                            metric.color === 'primary-active' ? 'bg-blue-100 text-blue-600' :
                            metric.color === 'emerald-600' ? 'bg-green-100 text-green-600' :
                            metric.color === 'orange-600' ? 'bg-orange-100 text-orange-600' :
                            'bg-purple-100 text-purple-600'
                        ]">
                            <i :class="metric.icon + ' text-xl'"></i>
                        </div>
                        <div>
                            <p class="text-sm text-surface-600 font-medium">{{ $t(metric.title) }}</p>
                            <p class="text-2xl font-bold text-surface-900">{{ metric.value.toLocaleString() }}</p>
                        </div>
                    </div>
                </div>
                <div class="flex items-center justify-between">
                    <div v-if="metric.title !== 'Recommended Sites'" class="flex items-center space-x-2">
                        <span :class="[
                            'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                            metric.increase ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        ]">
                            <i :class="[
                                'mr-1 text-xs',
                                metric.increase ? 'pi pi-arrow-up' : 'pi pi-arrow-down'
                            ]"></i>
                            {{ metric.percent }}%
                        </span>
                        <span class="text-xs text-surface-500">{{ $t('this month') }}</span>
                    </div>
                </div>
                <div v-if="metric.title === 'Recommended Sites'">
                    <a
                        :href="route('sites.index') + '?filter[recommended]=true'"
                        class="flex items-center justify-between gap-2 text-sm text-surface-600 hover:text-surface-800 hover:underline transition-all cursor-pointer"
                    >
                        <span>{{ $t('View Recommended Sites') }}</span>
                        <i class="pi pi-external-link text-surface-400 text-xs"></i>
                    </a>
                </div>
            </div>
        </div>

        <!-- Quick Stats -->
        <div class="col-span-12 md:col-span-4">
            <div class="bg-white rounded-xl p-6 shadow-sm border border-surface-200 h-full flex flex-col">
                <h3 class="text-lg font-semibold text-surface-900 mb-4">{{ $t('Quick Stats') }}</h3>
                <div class="space-y-4 flex-1">
                    <div
                        v-for="stat in quickStats"
                        :key="stat.label"
                        class="flex items-center justify-between p-3 rounded-lg border border-surface-100"
                    >
                        <div class="flex items-center space-x-3">
                            <div :class="[stat.bgColor, 'p-2 rounded-lg']">
                                <i :class="[stat.icon, stat.color, 'text-lg']"></i>
                            </div>
                            <span class="font-medium text-surface-900">{{ stat.label }}</span>
                        </div>
                        <span class="font-bold text-lg text-surface-900">{{ stat.value }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Recent Activity -->
        <div class="col-span-12 md:col-span-8">
            <div class="bg-white rounded-xl p-6 shadow-sm border border-surface-200 h-full flex flex-col">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-semibold text-surface-900">{{ $t('Recent Activity') }}</h3>
                    <Button
                        :label="$t('View all sites')"
                        severity="secondary"
                        text
                        size="small"
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('sites.index'), {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                </div>
                <div class="space-y-3 flex-1">
                    <div
                        v-for="activity in recentActivity"
                        :key="activity.id"
                        @click="() => {
                            showLoading($t('Loading sites...'));
                            const url = route('sites.index') + `?filter[url]=${encodeURIComponent(activity.title)}`;
                            router.visit(url, {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                        class="flex items-center space-x-3 p-3 rounded-lg hover:bg-surface-50 transition-colors cursor-pointer"
                    >
                        <div class="flex-shrink-0">
                            <i :class="[activity.icon, activity.iconClass, 'text-lg']"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="font-medium text-surface-900 truncate">{{ activity.title }}</p>
                            <p class="text-sm text-surface-600 truncate">{{ activity.description }}</p>
                        </div>
                        <div class="flex-shrink-0 text-xs text-surface-500">
                            {{ new Date(activity.time).toLocaleDateString() }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Brand New Opportunities -->
        <div class="col-span-12 lg:col-span-4">
            <div class="bg-white rounded-xl p-6 shadow-sm border border-surface-200">
                <div class="flex items-center space-x-2 mb-4">
                    <div class="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                        <i class="pi pi-sparkles text-teal-600 text-lg"></i>
                    </div>
                    <div>
                        <h3 class="text-lg font-semibold text-teal-500">{{ $t('Brand new opportunities') }}</h3>
                        <p class="text-sm text-surface-600">{{ $t('Latest additions onto our catalog') }}</p>
                    </div>
                </div>

                <div class="space-y-3">
                    <div
                        v-for="(site, index) in news.slice(0, 3)"
                        :key="index"
                        @click="() => {
                            showLoading($t('Loading sites...'));
                            const url = route('sites.index') + `?filter[url]=${encodeURIComponent(site.real_url || site.url)}`;
                            router.visit(url, {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                        class="flex items-center justify-between p-4 rounded-lg border border-surface-100 hover:border-primary hover:shadow-sm transition-all cursor-pointer"
                    >
                        <div class="flex-1">
                            <p class="font-medium text-surface-900 truncate mb-2">{{ site.real_url }}</p>
                            <div class="flex items-center space-x-2">
                                <span class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                    DR: {{ site.dr ?? '-' }}
                                </span>
                                <span class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                                    DA: {{ site.da ?? '-' }}
                                </span>
                                <span v-if="site.traffic !== null && site.traffic !== undefined"
                                    class="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full font-medium">
                                    {{ $t('Traffic') }}: {{ site.traffic?.toLocaleString() || 0 }}
                                </span>
                            </div>
                        </div>
                        <div class="flex items-center space-x-3 ml-4">
                            <i class="pi pi-chevron-right text-surface-400"></i>
                        </div>
                    </div>
                </div>

                <div class="text-right mt-4">
                    <Button
                        :label="$t('View all new sites')"
                        severity="secondary"
                        text
                        size="small"
                        @click="() => {
                            showLoading($t('Loading sites...'));
                            router.visit(route('sites.index') + '?filter[new]=true&sort=-dr', {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                </div>
            </div>
        </div>

        <!-- Top Sellers -->
        <div class="col-span-12 lg:col-span-4">
            <RecommendedSitesSection
                :sites="topSellers"
                title="Top Sellers"
                subtitle="Websites that deserve your attention"
                :max-items="3"
                route-name="sites.show"
                browse-route="sites.index"
                browse-filter="sort=-orders_count"
            />
        </div>

        <!-- Team Recommendations -->
        <div class="col-span-12 lg:col-span-4">
            <RecommendedSitesSection
                :sites="recommended"
                title="Our team recommendations"
                subtitle="Carefully selected websites for you"
                :max-items="3"
                route-name="sites.show"
                browse-route="sites.index"
                browse-filter="filter[recommended]=true&sort=-dr"
            />
        </div>

        <!-- Admin Tables (from DashboardNew list data) -->
        <div v-if="adminTables.length > 0" class="col-span-12">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div
                    v-for="(table, index) in adminTables.slice(0, 6)"
                    :key="index"
                    class="bg-white rounded-xl p-6 shadow-sm border border-surface-200"
                >
                    <h3 class="text-lg font-semibold text-surface-900 mb-4">{{ $t(table.title) }}</h3>
                    <div class="overflow-hidden">
                        <table class="w-full">
                            <thead>
                                <tr>
                                    <th
                                        v-for="(column, c) in table.columns"
                                        :key="c"
                                        class="whitespace-nowrap bg-surface-50 px-3 py-2 text-left text-sm font-semibold text-surface-900"
                                        scope="col"
                                    >
                                        {{ $t(column) }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-surface-200">
                                <tr
                                    v-for="(row, r) in table.data.slice(0, 5)"
                                    :key="r"
                                >
                                    <td
                                        v-for="(attribute, a) in table.attributes"
                                        :key="a"
                                        :class="['whitespace-nowrap px-3 py-2 text-sm', a === 0 ? 'font-medium text-surface-900' : 'text-surface-500']"
                                    >
                                        {{ row[attribute] }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>


        <!-- Analytics Section -->
        <Analytics
            class="col-span-12"
            :sites-by-country="sitesByCountry"
            :sites-by-country-svg="sitesByCountrySvg"
        />

        <!-- Quick Actions -->
        <div class="col-span-12">
            <div class="bg-white rounded-xl p-6 shadow-sm border border-surface-200">
                <h3 class="text-lg font-semibold text-surface-900 mb-4">{{ $t('Admin Quick Actions') }}</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Button
                        :label="$t('Sites')"
                        icon="pi pi-globe"
                        class="flex-col h-20"
                        severity="secondary"
                        outlined
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('sites.index'), {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                    <Button
                        :label="$t('Orders')"
                        icon="pi pi-shopping-cart"
                        class="flex-col h-20"
                        severity="secondary"
                        outlined
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('orders.index'), {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                    <Button
                        :label="$t('Clients')"
                        icon="pi pi-users"
                        class="flex-col h-20"
                        severity="secondary"
                        outlined
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('clients.index'), {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                    <Button
                        :label="$t('Sellers')"
                        icon="pi pi-users"
                        class="flex-col h-20"
                        severity="secondary"
                        outlined
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('sellers.index'), {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
